import { Check, Minus } from "lucide-react";
import { useLang } from "../i18n";

// Generic luxury price-comparison matrix.
// tiers: [{id,name,price}], rows: [{fr,en,inc:[]}]
export const PriceMatrix = ({ tiers, rows, highlight }) => {
  const { t, lang } = useLang();
  const onBook = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="overflow-x-auto pb-1">
      <div className="min-w-[640px]">
        {/* header */}
        <div
          className="grid gap-3 items-stretch"
          style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${tiers.length}, 1fr)` }}
        >
          <div className="flex items-end pb-5">
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500">
              {lang === "fr" ? "Prestations" : "Services"}
            </span>
          </div>
          {tiers.map((tier, i) => (
            <div
              key={tier.id}
              data-testid={`tier-head-${tier.id}`}
              className={`rounded-t-2xl px-5 pt-6 pb-5 text-center border-x border-t transition-colors ${
                highlight === i
                  ? "bg-gradient-to-b from-[var(--c-gold)]/20 to-[var(--c-gold)]/5 border-[var(--c-gold)]/70 shadow-[0_-8px_40px_-12px_rgba(212,175,55,0.5)]"
                  : "bg-[var(--c-bg-3)] border-[var(--c-gold)]/20"
              }`}
            >
              {highlight === i && (
                <span className="inline-block mb-2 text-[0.55rem] font-mono uppercase tracking-[0.25em] px-2.5 py-1 rounded-full bg-[var(--c-gold)] text-black font-bold">
                  ★ Top
                </span>
              )}
              <p className="font-display text-2xl text-white">{tier.name}</p>
              <p className="mt-2">
                <span className="text-[0.6rem] text-neutral-500 align-top mr-1">{t.pricing.from}</span>
                <span className="gold-text font-semibold text-2xl">{tier.price}</span>
                <span className="text-xs text-neutral-500 ml-1">{t.pricing.currency}</span>
              </p>
            </div>
          ))}
        </div>

        {/* rows */}
        <div>
          {rows.map((row, ri) => (
            <div
              key={ri}
              className="grid gap-3 items-center"
              style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${tiers.length}, 1fr)` }}
            >
              <div className="py-3.5 pr-4 text-sm text-neutral-200 border-b border-white/10 font-medium">
                {row[lang]}
              </div>
              {tiers.map((tier, ti) => (
                <div
                  key={tier.id}
                  className={`py-3.5 flex justify-center border-b border-x transition-colors ${
                    highlight === ti ? "bg-[var(--c-gold)]/[0.09] border-[var(--c-gold)]/40" : "border-[var(--c-gold)]/10"
                  }`}
                >
                  {row.inc[ti] ? (
                    <Check size={17} className="text-[var(--c-gold)]" strokeWidth={2.5} />
                  ) : (
                    <Minus size={15} className="text-neutral-700" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* footer cta */}
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${tiers.length}, 1fr)` }}
        >
          <div />
          {tiers.map((tier, ti) => (
            <div
              key={tier.id}
              className={`px-3 pt-5 pb-6 rounded-b-2xl border-x border-b ${
                highlight === ti
                  ? "bg-gradient-to-b from-[var(--c-gold)]/5 to-[var(--c-gold)]/15 border-[var(--c-gold)]/70 shadow-[0_18px_40px_-18px_rgba(212,175,55,0.5)]"
                  : "bg-[var(--c-bg-3)] border-[var(--c-gold)]/20"
              }`}
            >
              <button
                data-testid={`tier-book-${tier.id}`}
                onClick={onBook}
                className={`w-full text-xs font-semibold uppercase tracking-wider py-2.5 rounded-full transition-colors ${
                  highlight === ti
                    ? "bg-[var(--c-gold)] text-black hover:bg-[var(--c-gold-soft)]"
                    : "gold-border text-[var(--c-gold)] hover:bg-white/5"
                }`}
              >
                {t.pricing.book}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
